
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { PERMISSION } from '@/permissions';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';

const namespace: string = 'auth';

@Component<OrderModalActions>({
	components: {
		StandardModal,
		TextButton,
		WarningModal,
		InfoBanner
	}
})
export default class OrderModalActions extends Vue {
	@Getter('hasPermission', { namespace }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('getRestaurantDeliveryInfo', { namespace }) private restaurantDeliveryInfo!: Delivery;
	@Getter('isK12Location', { namespace }) private isK12Location!: boolean;
	@Getter('areApp8CreditsEnabled', { namespace }) private areApp8CreditsEnabled!: boolean;
	@Getter('isCateringRestaurant', { namespace }) private isCateringRestaurant!: boolean;
	@Getter('areCateringEventsEnabled', { namespace }) private areCateringEventsEnabled!: boolean;
	@Prop({ type: Boolean, required: true }) private isSuitesCateringEditing!: boolean;
	@Prop({ type: Boolean, required: true }) private disableSave!: boolean;
	@Prop({ type: Number, required: true }) private loadingItemIndex!: number;
	@Prop({ type: Object, required: true }) private order!: Order;
	@Prop({ type: Object, required: true }) private loading!: { [key: string]: boolean };

	private PERMISSION = PERMISSION;

	private get isAnyLoading(): boolean {
		return Object.values(this.loading).some(value => value === true) || this.loadingItemIndex > -1;
	}

	private get hasPriceOnRequestItems(): boolean {
		return this.isCateringRestaurant
			&& this.order.type === 'generic-catering'
			&& this.order.data.items.some((item: OrderItem) => item.price_on_request === true);
	}

	private get showRepeatOrderButton(): boolean {
		return this.order.type === 'generic-catering' && this.hasPermission(PERMISSION.ORDER_CREATE_CATERING);
	}

	// Check for suites catering orders if the event preordering is locked or not
	private get isOrderSuitesCateringAndNotLockedYet(): boolean {
		if (this.order.type === 'catering') {
			const { eventLockPeriod, eventStartDate } = this.order.data.suites!;
			const today: DateTime = DateTime.local();
			const preOrderLockDate: DateTime = DateTime.fromISO(eventStartDate).minus({ days: eventLockPeriod });
			if (today < preOrderLockDate) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Check if the order can have its delivery modified
	 * The order must:
	 * - be in progress
	 * - be a school order
	 * - have a school delivery type
	 * The location must:
	 * - have school delivery
	 * - have editable fields (grades, classrooms, teachers)
	 *
	 * @return {boolean}
	 */
	private get allowSchoolDeliveryModification(): boolean {
		if (!this.order.data.checkout.delivery) {
			return false;
		}
		const schools = this.restaurantDeliveryInfo?.schools;
		const schoolDeliverySettings = schools?.length === 1
			? schools[0]
			: schools?.find((school: SchoolDeliveryElement) => school.name === this.order.data.checkout.delivery?.school);

		return (
			this.order.status === 'in-progress'
			&& this.order.type === 'school'
			&& this.order.data.checkout.delivery.type === 'school'
			&& this.restaurantDeliveryInfo?.type === 'school'
			&& !!schoolDeliverySettings
			&& Object.keys(schoolDeliverySettings).some((key: string) => ['classrooms', 'grades', 'teachers'].includes(key))
		);
	}

	/**
	 * Show a cancel order button for:
	 * - Generic catering orders
	 * - K12 locations with credits enabled and allowed role (admin, enterprise or manager)
	 *
	 * @return {boolean}
	 */
	private get showCancelOrderButton(): boolean {
		const canCancelGenericCatering = this.order.type === 'generic-catering'
			&& this.hasPermission(PERMISSION.ORDER_CANCEL_CATERING);

		const canCancelSchoolToRefund = this.isK12Location
			&& this.areApp8CreditsEnabled
			&& this.order.type === 'school'
			&& this.hasPermission(PERMISSION.ORDER_CANCEL_SCHOOL_REFUND);

		return canCancelGenericCatering || canCancelSchoolToRefund;
	}

	/**
	 * Show the decline button for a pending order. Decline is available for all order
	 * types, but generic-catering and school orders follow the same role restrictions as
	 * cancel (manager+ for catering, ORDER_CANCEL_SCHOOL_REFUND for K12 school w/ credits).
	 *
	 * @return {boolean}
	 */
	private get showDeclineOrderButton(): boolean {
		if (this.order.type === 'generic-catering') {
			return this.hasPermission(PERMISSION.ORDER_CANCEL_CATERING);
		}

		if (this.isK12Location && this.areApp8CreditsEnabled && this.order.type === 'school') {
			return this.hasPermission(PERMISSION.ORDER_CANCEL_SCHOOL_REFUND);
		}

		return true;
	}

	private get canAccessCateringBeo(): boolean {
		return !!(this.order.type === 'generic-catering' && this.areCateringEventsEnabled) && this.hasPermission(PERMISSION.ROUTE_CATERING_BEO);
	}

	private redirectToOrderDetails(): void {
		this.$router.push(`/order-management/${this.order.id}`).catch(() => {});
	}

	private redirectToBeoPage(): void {
		this.$router.push(`/order-management/${this.order.id}/beo`).catch(() => {});
	}
}
